import * as React from 'react';
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-final-form';
import cn from 'classnames';

import { useTheme } from '../../../../theme';
import { TravellerFields } from '../../../../TravellerForm/types';
import { MOBILE_MIN_WIDTH } from '../../../../utils';
import { CdnIcon } from '@shared/ui';
import { CloseButton } from '../../../../BaseComponents';
import Link from '../../../../Link';

interface Props {
	passengerId: number;
	label: React.ReactNode;
	numberIcon?: React.ReactNode;
	correct: boolean;
	onClick?: () => void;
	passengerFieldsMap: {
		[name: string]: string;
	};
	open?: boolean;
	showErrorNotice?: boolean;
	hideClearButton?: boolean;
}

const Header: React.FC<Props> = ({
	passengerId,
	label,
	numberIcon,
	correct,
	onClick,
	passengerFieldsMap,
	open,
	showErrorNotice,
	hideClearButton
}) => {
	const { t } = useTranslation('Passenger');
	const theme = useTheme('PassengerForm').Header;

	const { mutators } = useForm();
	const isMobile = useMediaQuery({ maxWidth: MOBILE_MIN_WIDTH });

	const clearFieldsHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		if (e) {
			e.preventDefault();
			e.stopPropagation();
		}

		const mutator = mutators['clearFields'];
		mutator(passengerId);
	};

	return (
		<div
			className={cn(theme.header, {
				[theme.filled]: correct,
				[theme.collapsed]: !open,
				[theme.header_error]: showErrorNotice
			})}
			onClick={onClick}
		>
			<div className={theme.number}>{numberIcon || passengerId + 1}</div>
			<div className={theme.payload}>
				<div className={theme.passengerName}>{label}</div>

				{isMobile && showErrorNotice && <div className={theme.error}>{t('Please, check passenger data')}</div>}

				{isMobile && correct && passengerFieldsMap && (
					<>
						<div>
							{passengerFieldsMap[TravellerFields.Gender]},{' '}
							{passengerFieldsMap[TravellerFields.BirthDate]},{' '}
							{passengerFieldsMap[TravellerFields.Nationality]}
						</div>

						{passengerFieldsMap['docType'] && (
							<div>
								{passengerFieldsMap['docType']}: {passengerFieldsMap['docNumber']}
							</div>
						)}

						{passengerFieldsMap['docExpiryDate'] && (
							<div>
								{t('docExpiryDate')}: {passengerFieldsMap['docExpiryDate']}
							</div>
						)}

						{passengerFieldsMap['loyaltyNumber'] && (
							<div className={theme.loyalty_number}>
								{t('loyaltyNumber')}: {passengerFieldsMap['loyaltyNumber']}
							</div>
						)}
					</>
				)}
			</div>

			{correct && !numberIcon && <CdnIcon className={theme.successIcon} icon="Info/Item Icluded" />}

			{correct && !hideClearButton && (
				<>
					{isMobile ? (
						<CloseButton variant="closePopup" className={theme.clearButton} onClick={clearFieldsHandler} />
					) : (
						<Link action={clearFieldsHandler} noUderline className={theme.clearLink}>
							{t('clear')}
							<CdnIcon icon="Action/Close-Clear" />
						</Link>
					)}
				</>
			)}
		</div>
	);
};

export default Header;
